import React, { useState } from 'react';
import { useGym } from '../contexts/GymContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Building2, Plus, Check, Pencil, Trash2, Briefcase, Layers } from 'lucide-react';
import { toast } from 'sonner';

const BUSINESS_TYPES = [
  { value: 'academia', label: 'Academia / Fitness' },
  { value: 'varejo', label: 'Comércio / Varejo' },
  { value: 'servicos', label: 'Serviços Profissionais' },
  { value: 'restaurante', label: 'Restaurante / Alimentação' },
  { value: 'industria', label: 'Indústria' },
  { value: 'saude', label: 'Saúde / Clínicas' },
  { value: 'educacao', label: 'Educação' },
  { value: 'tecnologia', label: 'Tecnologia / Software' },
  { value: 'construcao', label: 'Construção Civil' },
  { value: 'logistica', label: 'Logística / Transporte' },
  { value: 'beleza', label: 'Beleza / Estética' },
  { value: 'consultoria', label: 'Consultoria' },
  { value: 'agronegocio', label: 'Agronegócio' },
  { value: 'outro', label: 'Outro' },
];

export const GymSelector = () => {
  const { gyms, selectedGym, selectGym, addGym, updateGym, deleteGym } = useGym();
  const navigate = useNavigate();
  const location = useLocation();
  const isGeral = location.pathname === '/geral';
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingGym, setEditingGym] = useState(null);
  const [newGymData, setNewGymData] = useState({ 
    name: '', 
    description: '',
    business_type: '',
    business_description: ''
  });
  const [loading, setLoading] = useState(false);

  const handleAddGym = async (e) => {
    e.preventDefault();
    if (!newGymData.business_type) {
      toast.error('Selecione o setor do negócio');
      return;
    }
    setLoading(true);
    try {
      const newGym = await addGym(newGymData);
      selectGym(newGym);
      setDialogOpen(false);
      setNewGymData({ name: '', description: '', business_type: '', business_description: '' });
    } catch (error) {
      // Error handled in context
    } finally {
      setLoading(false);
    }
  };

  const handleEditGym = async (e) => {
    e.preventDefault();
    if (!editingGym) return;
    
    setLoading(true);
    try {
      await updateGym(editingGym.id, newGymData);
      setEditDialogOpen(false);
      setEditingGym(null);
      setNewGymData({ name: '', description: '', business_type: '', business_description: '' });
    } catch (error) {
      // Error handled in context
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGym = async (gym) => {
    if (gym.is_default) {
      toast.error('Não é possível deletar a unidade padrão');
      return;
    }
    
    if (window.confirm(`Tem certeza que deseja deletar "${gym.name}"? Todos os dados dessa unidade serão perdidos!`)) {
      try {
        await deleteGym(gym.id);
        setDropdownOpen(false);
      } catch (error) {
        // Error handled in context
      }
    }
  };

  const openEditDialog = (gym) => {
    setEditingGym(gym);
    setNewGymData({ 
      name: gym.name, 
      description: gym.description || '',
      business_type: gym.business_type || '',
      business_description: gym.business_description || ''
    });
    setEditDialogOpen(true);
    setDropdownOpen(false);
  };

  if (!selectedGym) return null;

  return (
    <>
      <div className="relative">
        <button
          data-testid="gym-selector-button"
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className="flex items-center gap-3 px-4 py-3 w-full rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#22C55E]/30 transition-all group"
        >
          <div className="p-2 rounded-lg bg-[#22C55E]/10 group-hover:bg-[#22C55E]/20 transition-colors">
            {isGeral ? <Layers className="text-[#22C55E]" size={20} /> : <Building2 className="text-[#22C55E]" size={20} />}
          </div>
          <div className="flex-1 text-left min-w-0">
            <p className="text-xs text-zinc-500 uppercase tracking-wider">{isGeral ? 'Visão' : 'Unidade Selecionada'}</p>
            <p className="text-white font-bold truncate">{isGeral ? 'Geral (Consolidado)' : selectedGym.name}</p>
          </div>
          <svg
            className={`w-4 h-4 text-zinc-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown */}
        {dropdownOpen && (
          <>
            <div
              className="fixed inset-0 z-30"
              onClick={() => setDropdownOpen(false)}
            />
            <div className="absolute top-full left-0 right-0 mt-2 z-40 bg-zinc-900 border border-white/10 rounded-lg shadow-2xl overflow-hidden max-h-80 overflow-y-auto custom-scrollbar">
              <div className="p-2">
                {gyms.length > 1 && (
                  <button
                    data-testid="gym-option-geral"
                    onClick={() => {
                      setDropdownOpen(false);
                      navigate('/geral');
                    }}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg mb-1 text-left transition-all border ${
                      isGeral
                        ? 'bg-[#22C55E]/10 border-[#22C55E]/30'
                        : 'border-transparent hover:bg-white/5'
                    }`}
                  >
                    <Layers className={isGeral ? 'text-[#22C55E]' : 'text-zinc-400'} size={18} />
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium ${isGeral ? 'text-[#22C55E]' : 'text-white'}`}>Geral (Consolidado)</p>
                      <p className="text-xs text-zinc-500 truncate">Resumo somado das unidades</p>
                    </div>
                    {isGeral && <Check className="text-[#22C55E] flex-shrink-0" size={18} />}
                  </button>
                )}
                {gyms.length > 1 && <div className="border-t border-white/10 my-1" />}
                {gyms.map((gym) => (
                  <div
                    key={gym.id}
                    className={`group rounded-lg transition-all mb-1 ${
                      selectedGym.id === gym.id
                        ? 'bg-[#22C55E]/10 border border-[#22C55E]/30'
                        : 'hover:bg-white/5'
                    }`}
                  >
                    <div className="flex items-center justify-between p-3">
                      <button
                        data-testid={`gym-option-${gym.id}`}
                        onClick={() => {
                          selectGym(gym);
                          setDropdownOpen(false);
                          toast.success(`Mudou para: ${gym.name}`);
                          if (isGeral) navigate('/dashboard');
                        }}
                        className="flex-1 flex items-center gap-3 text-left"
                      >
                        <Building2
                          className={selectedGym.id === gym.id ? 'text-[#22C55E]' : 'text-zinc-400'}
                          size={18}
                        />
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium truncate ${selectedGym.id === gym.id ? 'text-[#22C55E]' : 'text-white'}`}>
                            {gym.name}
                          </p>
                          {gym.description && (
                            <p className="text-xs text-zinc-500 truncate">{gym.description}</p>
                          )}
                        </div>
                        {selectedGym.id === gym.id && (
                          <Check className="text-[#22C55E] flex-shrink-0" size={18} />
                        )}
                      </button>
                      
                      <div className="flex gap-1 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditDialog(gym);
                          }}
                          className="p-2 rounded-md hover:bg-[#22C55E]/10 text-zinc-400 hover:text-[#22C55E] transition-colors"
                          title="Editar"
                        >
                          <Pencil size={14} />
                        </button>
                        {!gym.is_default && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteGym(gym);
                            }}
                            className="p-2 rounded-md hover:bg-red-500/10 text-zinc-400 hover:text-red-400 transition-colors"
                            title="Excluir"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-white/10 p-2">
                <button
                  data-testid="add-gym-button"
                  onClick={() => {
                    setDialogOpen(true);
                    setDropdownOpen(false);
                  }}
                  className="w-full flex items-center gap-2 p-3 rounded-lg hover:bg-[#22C55E]/10 text-[#22C55E] font-medium transition-all"
                >
                  <Plus size={18} />
                  <span>Adicionar Nova Unidade</span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add Gym Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading text-[#22C55E] uppercase flex items-center gap-2">
              <Building2 size={24} />
              Nova Unidade
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddGym} className="space-y-4 mt-4">
            <div>
              <Label className="text-zinc-300">Nome da Unidade *</Label>
              <Input
                data-testid="new-gym-name-input"
                value={newGymData.name}
                onChange={(e) => setNewGymData({ ...newGymData, name: e.target.value })}
                className="input-field mt-1"
                placeholder="Ex: Filial Centro"
                required
              />
            </div>
            
            <div>
              <Label className="text-zinc-300 flex items-center gap-2">
                <Briefcase size={16} className="text-[#22C55E]" />
                Setor do Negócio *
              </Label>
              <select
                data-testid="new-gym-business-type"
                value={newGymData.business_type}
                onChange={(e) => setNewGymData({ ...newGymData, business_type: e.target.value })}
                className="input-field mt-1 w-full"
                required
              >
                <option value="">Selecione o setor...</option>
                {BUSINESS_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              <p className="text-xs text-zinc-500 mt-1">
                O Diretor Financeiro usará benchmarks específicos do seu setor
              </p>
            </div>
            
            <div>
              <Label className="text-zinc-300">Descrição do Negócio</Label>
              <textarea
                data-testid="new-gym-business-description"
                value={newGymData.business_description}
                onChange={(e) => setNewGymData({ ...newGymData, business_description: e.target.value })}
                className="input-field mt-1 w-full min-h-[80px] resize-none"
                placeholder="Descreva seu negócio: produtos/serviços oferecidos, público-alvo, diferenciais..."
                rows={3}
              />
              <p className="text-xs text-zinc-500 mt-1">
                Quanto mais detalhes, melhor será a análise do Diretor Financeiro
              </p>
            </div>
            
            <div>
              <Label className="text-zinc-300">Descrição da Unidade (opcional)</Label>
              <Input
                data-testid="new-gym-description-input"
                value={newGymData.description}
                onChange={(e) => setNewGymData({ ...newGymData, description: e.target.value })}
                className="input-field mt-1"
                placeholder="Ex: Unidade do centro da cidade"
              />
            </div>
            
            <Button
              data-testid="save-new-gym-button"
              type="submit"
              disabled={loading}
              className="w-full btn-primary"
            >
              {loading ? 'Salvando...' : 'Adicionar Unidade'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Gym Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => {
        setEditDialogOpen(open);
        if (!open) {
          setEditingGym(null);
          setNewGymData({ name: '', description: '', business_type: '', business_description: '' });
        }
      }}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading text-[#22C55E] uppercase flex items-center gap-2">
              <Pencil size={24} />
              Editar Unidade
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditGym} className="space-y-4 mt-4">
            <div>
              <Label className="text-zinc-300">Nome da Unidade *</Label>
              <Input
                data-testid="edit-gym-name-input"
                value={newGymData.name}
                onChange={(e) => setNewGymData({ ...newGymData, name: e.target.value })}
                className="input-field mt-1"
                placeholder="Ex: Filial Centro"
                required
              />
            </div>
            
            <div>
              <Label className="text-zinc-300 flex items-center gap-2">
                <Briefcase size={16} className="text-[#22C55E]" />
                Setor do Negócio *
              </Label>
              <select
                data-testid="edit-gym-business-type"
                value={newGymData.business_type}
                onChange={(e) => setNewGymData({ ...newGymData, business_type: e.target.value })}
                className="input-field mt-1 w-full"
                required
              >
                <option value="">Selecione o setor...</option>
                {BUSINESS_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <Label className="text-zinc-300">Descrição do Negócio</Label>
              <textarea
                data-testid="edit-gym-business-description"
                value={newGymData.business_description}
                onChange={(e) => setNewGymData({ ...newGymData, business_description: e.target.value })}
                className="input-field mt-1 w-full min-h-[80px] resize-none"
                placeholder="Descreva seu negócio: produtos/serviços oferecidos, público-alvo, diferenciais..."
                rows={3}
              />
            </div>
            
            <div>
              <Label className="text-zinc-300">Descrição da Unidade (opcional)</Label>
              <Input
                data-testid="edit-gym-description-input"
                value={newGymData.description}
                onChange={(e) => setNewGymData({ ...newGymData, description: e.target.value })}
                className="input-field mt-1"
                placeholder="Ex: Unidade do centro da cidade"
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                type="button"
                onClick={() => setEditDialogOpen(false)}
                className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white"
              >
                Cancelar
              </Button>
              <Button
                data-testid="save-edit-gym-button"
                type="submit"
                disabled={loading}
                className="flex-1 btn-primary"
              >
                {loading ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
